// ActivityDetailCollapse.jsx

import React, { useMemo } from 'react';

// Utilities
import { formatDateTime } from 'utilities/DateTime';
import { getActivityActorName } from 'node/activity/utils/formatActivitySystemMessages';
import { toActivityTimestampMs } from 'node/activity/utils/buildActivityFeedItems';
import { getActivityEventKey } from 'node/activity/utils/activityReactions';

// CSS
import './ActivitySystemEvent.css';
import './ActivityDetailCollapse.css';

// Components
import ActivitySystemEvent from 'node/activity/items/ActivitySystemEvent';

/**
 * ActivityDetailCollapse
 *
 * Collapsed run of host-lifted workflow check-offs.
 *
 * @param {object} props
 * @param {object} props.item - Collapse feed item.
 * @param {boolean} props.expanded
 * @param {function} props.onToggle
 * @param {Record<string, string>} [props.fieldLabels]
 * @param {Record<string, object>} [props.fieldDefs]
 * @param {string} [props.typeLabel]
 * @param {object|null} [props.node]
 * @param {Map<string, object[]>} [props.reactionsByEventKey]
 * @param {'datetime'|'time'} [props.timestampStyle]
 * @returns {ReactNode}
 */
const ActivityDetailCollapse = React.memo(({
  item,
  expanded,
  onToggle,
  fieldLabels = {},
  fieldDefs = {},
  typeLabel = '',
  timestampStyle = 'datetime',
  node = null,
  reactionsByEventKey = null,
}) => {
  const events = Array.isArray(item?.events) ? item.events : [];
  const count = Number(item?.count) || events.length;
  const stepTitle = String(item?.stepTitle || '').trim();
  const firstEvent = events[0] || null;
  const actorName = getActivityActorName(firstEvent);

  const summary = useMemo(() => {
    const noun = count === 1 ? 'item' : 'items';
    if (stepTitle) return `completed ${count} ${noun} on ${stepTitle}.`;
    return `completed ${count} checklist ${noun}.`;
  }, [count, stepTitle]);

  const occurredMs = toActivityTimestampMs(firstEvent?.occurredAt)
    || toActivityTimestampMs(firstEvent?.timestamp)
    || Number(item?.at) || 0;
  const timestampLabel = occurredMs
    ? formatDateTime(
      new Date(occurredMs),
      timestampStyle === 'time'
        ? { intlOptions: { hour: 'numeric', minute: '2-digit', hour12: true } }
        : undefined
    )
    : '';

  return (
    <div className="activity-detail-collapse-container">
      <button
        className="activity-system-event-container activity-detail-collapse-toggle"
        type="button"
        onClick={onToggle}
      >
        <div className="activity-system-event-row">
          <div className="activity-system-event-main">
            <div className="activity-system-event-body">
              <span className="activity-system-event-bullet">•</span>
              <span className="activity-system-event-copy">
                {actorName ? (
                  <span className="activity-system-event-actor">{actorName}</span>
                ) : null}
                <span className="activity-system-event-message">{summary}</span>
              </span>
            </div>
            <div className="activity-detail-collapse-hint">
              {expanded ? 'Hide details' : 'Show details'}
            </div>
          </div>
          {timestampLabel ? (
            <div className="activity-system-event-time">{timestampLabel}</div>
          ) : null}
        </div>
      </button>

      {expanded ? (
        <div className="activity-detail-collapse-items">
          {events.map((event) => (
            <ActivitySystemEvent
              key={event?.key || event?.id}
              event={event}
              fieldDefs={fieldDefs}
              fieldLabels={fieldLabels}
              node={node}
              reactions={reactionsByEventKey?.get(getActivityEventKey(event)) || []}
              timestampStyle={timestampStyle}
              typeLabel={typeLabel}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
});

ActivityDetailCollapse.displayName = 'ActivityDetailCollapse';

export default ActivityDetailCollapse;
